/**
 * Cache local sobre IndexedDB para lotes de datos historicos.
 *
 * Por que IndexedDB y no sessionStorage: un mes de ventas pesa ~1 MB y
 * sessionStorage topa alrededor de 5 MB por origen, asi que tres meses ya
 * lo revientan. IndexedDB no tiene ese techo y guarda objetos sin
 * serializar a mano.
 *
 * Solo se cachean ventanas de tiempo YA CERRADAS (meses pasados), que por
 * definicion no cambian. El mes en curso nunca se cachea.
 */

const DB_NOMBRE = "sigvem-cache";
const DB_VERSION = 1;
const ALMACEN = "lotes";

/** Dias que vive un lote antes de considerarse vencido. */
const VIDA_MS = 7 * 24 * 60 * 60 * 1000;

let promesaDb = null;

const abrirDb = () => {
    if (promesaDb) return promesaDb;

    promesaDb = new Promise((resolve, reject) => {
        if (typeof indexedDB === "undefined") {
            reject(new Error("IndexedDB no disponible"));
            return;
        }

        const peticion = indexedDB.open(DB_NOMBRE, DB_VERSION);

        peticion.onupgradeneeded = () => {
            const db = peticion.result;
            if (!db.objectStoreNames.contains(ALMACEN)) {
                const almacen = db.createObjectStore(ALMACEN, { keyPath: "clave" });
                almacen.createIndex("guardadoEn", "guardadoEn");
            }
        };

        peticion.onsuccess = () => resolve(peticion.result);
        peticion.onerror = () => reject(peticion.error);
    });

    // Si falla una vez no dejamos la promesa rechazada en memoria: el
    // siguiente intento vuelve a probar.
    promesaDb.catch(() => {
        promesaDb = null;
    });

    return promesaDb;
};

const enTransaccion = async (modo, fn) => {
    const db = await abrirDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(ALMACEN, modo);
        const almacen = tx.objectStore(ALMACEN);
        let resultado;
        try {
            resultado = fn(almacen);
        } catch (e) {
            reject(e);
            return;
        }
        tx.oncomplete = () => resolve(resultado);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
    });
};

const pedir = (peticion) =>
    new Promise((resolve, reject) => {
        peticion.onsuccess = () => resolve(peticion.result);
        peticion.onerror = () => reject(peticion.error);
    });

/**
 * Lee un lote. Devuelve null si no existe, si vencio o si IndexedDB no
 * esta disponible (modo privado, almacenamiento bloqueado).
 */
export const leerLote = async (clave) => {
    try {
        const registro = await enTransaccion("readonly", (almacen) =>
            pedir(almacen.get(clave))
        );
        const fila = await registro;
        if (!fila) return null;

        if (Date.now() - fila.guardadoEn > VIDA_MS) {
            borrarLote(clave);
            return null;
        }

        return fila.datos;
    } catch (e) {
        return null;
    }
};

/** Guarda un lote. Nunca lanza: si no se puede cachear, se sigue sin cache. */
export const guardarLote = async (clave, datos) => {
    try {
        await enTransaccion("readwrite", (almacen) =>
            almacen.put({ clave, datos, guardadoEn: Date.now() })
        );
        return true;
    } catch (e) {
        return false;
    }
};

export const borrarLote = async (clave) => {
    try {
        await enTransaccion("readwrite", (almacen) => almacen.delete(clave));
        return true;
    } catch (e) {
        return false;
    }
};

/** Vacia la cache completa. Util tras registrar un pago o anular una venta. */
export const vaciarCache = async () => {
    try {
        await enTransaccion("readwrite", (almacen) => almacen.clear());
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Borra los lotes cuya clave empiece con el prefijo dado.
 * Se usa para invalidar solo una familia de consultas (p. ej. "ventas")
 * cuando algo cambia, sin tirar el resto de la cache.
 */
export const invalidarPrefijo = async (prefijo) => {
    try {
        const db = await abrirDb();
        return new Promise((resolve) => {
            const tx = db.transaction(ALMACEN, "readwrite");
            const almacen = tx.objectStore(ALMACEN);
            const cursor = almacen.openCursor();
            cursor.onsuccess = () => {
                const c = cursor.result;
                if (!c) return;
                if (typeof c.key === "string" && c.key.startsWith(prefijo)) {
                    c.delete();
                }
                c.continue();
            };
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => resolve(false);
            tx.onabort = () => resolve(false);
        });
    } catch (e) {
        return false;
    }
};
